import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { analysisClient } from "@/shared/lib/supabase";
import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import {
  MATRIX_PREVIEW_ROWS,
  shouldPollMatrixDetail,
  toMatrixListItem,
  toMatrixUi,
  type MatrixDetailRaw,
  type MatrixListRow,
} from "./mappers";
import type { MatrixListItemUi, MatrixUi } from "./types";
import { MatrixCompatibilityError } from "./types";

async function fetchMatrixListRows(): Promise<MatrixListRow[]> {
  const { data, error } = await analysisClient
    .from("tenant_collection")
    .select(
      "*, tenant_collection_setting(settings), analysis(count), preview:analysis(analysis_id, record_created, analysis_setting!inner(display_input_results), analysisstep(analysis_step_name, value))",
    )
    .order("record_created", { referencedTable: "preview", ascending: true })
    .limit(MATRIX_PREVIEW_ROWS, { referencedTable: "preview" });

  if (error) throw error;
  return data ?? [];
}

async function fetchMatrixList(): Promise<MatrixListItemUi[]> {
  const rows = await fetchMatrixListRows();

  return rows
    .map(toMatrixListItem)
    .filter((item): item is MatrixListItemUi => item !== null)
    .sort((a, b) => a.name.localeCompare(b.name));
}

async function fetchMatrixDetailRaw(
  collectionId: string,
): Promise<MatrixDetailRaw> {
  const [collectionResult, analysesResult, signaturesResult] =
    await Promise.all([
      analysisClient
        .from("tenant_collection")
        .select("*, tenant_collection_setting(settings)")
        .eq("tenant_collection_id", collectionId)
        .single(),
      analysisClient
        .from("analysis")
        .select(
          "*, analysis_setting!inner(display_name, display_input_results), analysisstep(analysis_step_id, analysis_step_name, analysis_step_status, value, analysis_order, analysisstep_setting(settings))",
        )
        .eq("tenant_collection_id", collectionId)
        .eq("analysis_type", "matrix")
        .order("record_created", { ascending: true }),
      analysisClient.rpc("get_matrix_row_signatures", {
        p_tenant_collection_id: collectionId,
      }),
    ]);

  if (collectionResult.error) throw collectionResult.error;
  if (analysesResult.error) throw analysesResult.error;
  if (signaturesResult.error) throw signaturesResult.error;

  return {
    collection: collectionResult.data,
    analyses: analysesResult.data ?? [],
    signatures: signaturesResult.data ?? [],
  };
}

async function fetchMatrixDetail(collectionId: string): Promise<MatrixUi> {
  return toMatrixUi(await fetchMatrixDetailRaw(collectionId));
}

export const matrixQueries = {
  all: getKeyOnlyQueryConfig(["matrix"]),

  lists: () => getKeyOnlyQueryConfig([...matrixQueries.all.queryKey, "list"]),

  list: () =>
    queryOptions({
      queryKey: matrixQueries.lists().queryKey,
      queryFn: fetchMatrixList,
    }),

  details: () =>
    getKeyOnlyQueryConfig([...matrixQueries.all.queryKey, "detail"]),

  detail: (collectionId: string) =>
    queryOptions({
      queryKey: [...matrixQueries.details().queryKey, collectionId],
      queryFn: () => fetchMatrixDetail(collectionId),
      retry: (failureCount, error) =>
        !(error instanceof MatrixCompatibilityError) && failureCount < 3,
      enabled: Boolean(collectionId),
      placeholderData: keepPreviousData,
      refetchInterval: (query) => {
        const data = query.state.data;
        return data && shouldPollMatrixDetail(data) ? 5000 : false;
      },
      refetchIntervalInBackground: true,
    }),
};
